import React, { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { MessageBundle } from '@amzn/arb-tools';
import DropdownController from '../../../shared/components/form/DropdownController';
import InputController from '../../../shared/components/form/InputController';
import { RequestPageContext } from '../../../shared/services/activation/activation';
import { COUNTRY_LABEL_KEY, STATE_LABEL_KEY } from '../constants';

interface CountryStateFieldProps {
  intl: MessageBundle;
  context: RequestPageContext;
  selectedCountry: string;
  selectPlaceholder: string;
  requiredMessage: string;
}

const CountryStateField: FC<CountryStateFieldProps> = ({
  intl,
  context,
  selectedCountry,
  selectPlaceholder,
  requiredMessage,
}) => {
  const { setValue } = useFormContext();

  const billingCountryItems = useMemo(
    () =>
      (context.billingCountries || []).map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    [context.billingCountries],
  );

  const billingCountry = (context.billingCountries || []).find(
    (c) => c.isoCode === selectedCountry,
  );
  const stateItems = useMemo(
    () =>
      (billingCountry?.states || []).map((s) => ({
        value: s.isoCode,
        label: s.name,
      })),
    [billingCountry?.states],
  );
  const hasStates = stateItems.length > 0;

  const handleCountryChange = (value: string) => {
    setValue('state', '');
    setValue('stateName', '');
    setValue('country', value);
  };

  const handleStateChange = (value: string) => {
    setValue('state', value);
    const selectedState = (billingCountry?.states || []).find(
      (s) => s.isoCode === value,
    );
    setValue('stateName', selectedState?.name || '');
  };

  return (
    <>
      <DropdownController
        label={intl.getMessage(COUNTRY_LABEL_KEY)}
        overrideDropdownLabel={selectPlaceholder}
        id="address-country-isoCode"
        controllerName="country"
        items={billingCountryItems}
        registerOptions={{ required: requiredMessage }}
        callback={handleCountryChange}
      />
      {hasStates ? (
        <DropdownController
          label={intl.getMessage(STATE_LABEL_KEY)}
          overrideDropdownLabel={selectPlaceholder}
          id="address-state-dropdown"
          controllerName="state"
          items={stateItems}
          registerOptions={{ required: requiredMessage }}
          callback={handleStateChange}
        />
      ) : (
        <InputController
          label={intl.getMessage(STATE_LABEL_KEY)}
          id="address-state-input"
          controllerName="state"
          registerOptions={{ required: requiredMessage }}
        />
      )}
    </>
  );
};

export default CountryStateField;
